import { useEffect, useRef, useState } from 'react';

/**
 * Tracks the element matching `selector`. The observer stays connected past the
 * first match so a replaced node (same id, different `<Route>`) is re-resolved
 * instead of leaving the caller with a detached element.
 */
export const useWaitForElement = (selector: string | null): Element | null => {
  const [element, setElement] = useState<Element | null>(null);
  // Mirrors the state so unrelated DOM churn (most of what the observer sees,
  // since it has to watch the whole body subtree) schedules no React work.
  const elementRef = useRef<Element | null>(null);

  useEffect(() => {
    const setResolvedElement = (found: Element | null): void => {
      if (found === elementRef.current) return;
      elementRef.current = found;
      setElement(found);
    };

    if (!selector) {
      setResolvedElement(null);
      return () => {};
    }

    const syncElement = (): void => {
      setResolvedElement(document.querySelector(selector));
    };

    syncElement();

    const observer = new MutationObserver(syncElement);

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [selector]);

  return element;
};
